<script setup lang="ts">
import { cn } from "@/lib/utils";
import { useWordProcessorStore } from "~/components/Base/WordProcessor/useWordProcessorStore";

const props = defineProps({
  dressUp: {
    type: Object as PropType<{
      text: string;
      startIndex: number;
      attributes: any;
    }>,
    required: true,
  },
  unassignedColor: {
    type: String,
    default: "bg-muted border-muted-foreground/30",
  },
  assignedColor: {
    type: String,
    default: "bg-primary/20 border-primary",
  },
  unassignedHoverColor: {
    type: String,
    default: "hover:bg-muted-foreground/10 hover:border-muted-foreground/50",
  },
  assignedHoverColor: {
    type: String,
    default: "hover:bg-primary/30 hover:border-primary",
  },
});

const documentId = inject("documentId") as any;
const selfAssessmentStore = useDocumentSelfAssessmentStore(documentId);
const { allSteps } = storeToRefs(selfAssessmentStore);

const wordProcessorStore = useWordProcessorStore(documentId);
const { selectionRange, editorRef } = storeToRefs(wordProcessorStore);

const dressUpSteps = computed(() => {
  return allSteps.value.filter((step) => {
    return step.validationAttributes?.underline == true;
  });
});

const open = ref(false);

const selectedStep = computed(() => {
  return dressUpSteps.value.find((step) => {
    return (
      props.dressUp.attributes[step.criteriaAttribute.attribute] != undefined
    );
  });
});

const popoverController = useNewDialogController();

const onSelect = (step: any) => {
  popoverController.value.close();

  if (selectedStep.value?.identifier === step.identifier) {
    return;
  }

  const quill = wordProcessorStore.getQuillInstance();
  if (!quill) return;

  // Remove other dress-up attributes
  dressUpSteps.value.forEach((s) => {
    if (props.dressUp.attributes[s.criteriaAttribute.attribute]) {
      quill.formatText(
        props.dressUp.startIndex,
        props.dressUp.text.length,
        s.criteriaAttribute.attribute,
        false
      );
    }
  });

  // Apply the new one
  quill.formatText(
    props.dressUp.startIndex,
    props.dressUp.text.length,
    step.criteriaAttribute.attribute,
    useGenerateUID()
  );

  open.value = false;
};

// Add a clear option if needed, though user didn't explicitly ask for it,
// toggling behavior in previous iteration was useful.
// But here we are moving items between lists, so "None" might be useful to move back to unclassified.
const onClear = () => {
  popoverController.value.close();

  const quill = wordProcessorStore.getQuillInstance();
  if (!quill) return;

  dressUpSteps.value.forEach((s) => {
    if (props.dressUp.attributes[s.criteriaAttribute.attribute]) {
      quill.formatText(
        props.dressUp.startIndex,
        props.dressUp.text.length,
        s.criteriaAttribute.attribute,
        false
      );
    }
  });
  open.value = false;
};
</script>

<template>
  <BasePopover
    v-model:open="open"
    align="start"
    :controller="popoverController"
  >
    <div
      :class="
        cn(
          'flex flex-row items-center px-1.5 py-0.5 text-card-foreground group transition-colors border rounded-full cursor-pointer',
          selectedStep
            ? `${assignedColor} ${assignedHoverColor}`
            : `${unassignedColor} ${unassignedHoverColor}`
        )
      "
    >
      <span class="font-medium text-xs truncate" :title="dressUp.text">
        {{ dressUp.text }}
      </span>
      <BaseIconChevronCircleDown class="text-[14px] opacity-50 ml-1" />
    </div>
    <template #content>
      <DocumentStudentEditingDressupsSelectorOptions
        :selectedAttribute="selectedStep?.criteriaAttribute.attribute"
        @select="onSelect"
        @clear="onClear"
      />
    </template>
  </BasePopover>
</template>

<style scoped></style>
